import React, { useMemo } from 'react';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { SplineChartV2, SplineChartV2Props, SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';
import SplineChartLoading, {
  SplineChartLoadingProps
} from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';

import { useAppSelector, useWeekRange, WeekRange, useMetricFormatter } from 'src/utils/Hooks';
import { useDorisMetricByWeekId, UseDorisMetricByWeekIdArgs } from 'src/serverProxy/useDorisMetricByWeekId';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertSplineChartV2SeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/splineChartV2';
import saveAs from 'file-saver';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { UseDorisDataProps } from 'src/utils/Hooks/useDorisData';
import { calculatePercentChange } from 'src/utils/app';
import { timestampToWeekId } from 'src/utils/dateUtils';

export interface AtlasV2DorisSplineChartProps
  extends Pick<
      BeaconChartWithLegendProps,
      'singleDropdownProps' | 'buildDropdownMenuOptions' | 'dropdownOptionHandler'
    >,
    UseDorisMetricByWeekIdArgs {
  metricType: string;
  queryKey: string;
  title?: string;
  showLastWeekValue?: boolean;
  showTitleSummary?: boolean;
  entityType?: string;
  splineChartLoadingProps?: SplineChartLoadingProps;
  beaconChartWithLegendOverride?: (
    props: Omit<BeaconChartWithLegendProps, 'children'>,
    metricByValues: ReturnType<typeof useDorisMetricByWeekId>
  ) => Omit<BeaconChartWithLegendProps, 'children'>;
  splineChartProps?: Partial<SplineChartV2Props>;
  lastWeekValue?: boolean;
  showMarketShare?: boolean;
  marketShareNumeratorRequestBuilder?: (
    params: Omit<UseDorisDataProps, 'metricType'> | 'branding'
  ) => UseDorisDataProps;
  methodName?: string;
  showAverageRate?: boolean;
}

/**
 * Converts V1 data format [timestamp, value][] to SplineChartV2Series format
 */
const convertToV2Series = (
  data: [number, number][],
  id: string,
  name: string
): SplineChartV2Series => ({
  id,
  name,
  data: data.map(([timestamp, value]) => ({
    weekId: timestampToWeekId(timestamp),
    value
  }))
});

const AtlasV2DorisSplineChart = ({
  queryKey,
  metricType,
  title,
  showLastWeekValue = false,
  showAverageRate = false,
  showTitleSummary = true,
  splineChartLoadingProps,
  metricTypeDisplay,
  showMarketShare,
  methodName,
  buildDropdownMenuOptions,
  dropdownOptionHandler,
  beaconChartWithLegendOverride,
  splineChartProps,
  marketShareNumeratorRequestBuilder,
  marketShareRequestBuilder,
  ...chartWithLegendProps
}: AtlasV2DorisSplineChartProps) => {
  const formatMetric = useMetricFormatter();
  const weekRange = useWeekRange();
  const useLatestWeek = showLastWeekValue || weekRange === WeekRange.LAST_WEEK;

  const comparisonTimePeriod = useAppSelector((s) => s.comparisonTimePeriod);
  const mainTimePeriod = useAppSelector((s) => s.mainTimePeriod);

  const metricByValues = useDorisMetricByWeekId({
    queryKey: queryKey || '',
    metricType,
    showMarketShare,
    resultCount: 1200,
    metricTypeDisplay,
    marketShareNumeratorRequestBuilder,
    marketShareRequestBuilder,
    methodName
  });

  const {
    primaryData,
    secondaryData,
    isLoading,
    formattedPrimaryTotal,
    formattedPrimaryAverageRate,
    formattedSecondaryAverageRate,
    formattedSecondaryTotal,
    percentChange,
    formattedLastPrimaryValue,
    formattedLastSecondaryValue,
    percentChangeLastValues,
    marketShareSplineData,
    marketShare,
    comparisonMarketShareSplineData,
    primaryAverageRate,
    secondaryAverageRate
  } = metricByValues;

  const chartPrimaryData = showMarketShare && marketShareSplineData ? marketShareSplineData : primaryData;
  const chartSecondaryData =
    showMarketShare && comparisonMarketShareSplineData ? comparisonMarketShareSplineData : secondaryData;

  // Use Redux display names - these are computed from the actual time period dates
  const primaryLegendDisplayName = mainTimePeriod.displayName;
  const comparisonLegendDisplayName = comparisonTimePeriod.displayName;

  // Convert to SplineChartV2 format
  const primarySeries = useMemo(
    () => convertToV2Series(chartPrimaryData, `${queryKey}-primary`, primaryLegendDisplayName),
    [chartPrimaryData, queryKey, primaryLegendDisplayName]
  );

  const comparisonSeries = useMemo(
    () => convertToV2Series(chartSecondaryData, `${queryKey}-comparison`, comparisonLegendDisplayName),
    [chartSecondaryData, queryKey, comparisonLegendDisplayName]
  );

  const getMainAndSecondaryMetrics = () => {
    if (showAverageRate) {
      if (weekRange === WeekRange.LAST_WEEK) {
        return [formattedLastPrimaryValue, formattedLastSecondaryValue, percentChangeLastValues];
      }

      return [
        formattedPrimaryAverageRate,
        formattedSecondaryAverageRate,
        calculatePercentChange(primaryAverageRate, secondaryAverageRate)
      ];
    }

    if (showMarketShare) {
      if (weekRange === WeekRange.LAST_WEEK) {
        return [formattedLastPrimaryValue, formattedLastSecondaryValue, percentChangeLastValues];
      }

      return [
        marketShare.primary.formattedMarketShareValue,
        marketShare.comparison.formattedMarketShareValue,
        calculatePercentChange(marketShare.primary.marketShareValue, marketShare.comparison.marketShareValue)
      ];
    }

    if (!useLatestWeek) {
      return [formattedPrimaryTotal, formattedSecondaryTotal, percentChange];
    }

    return [formattedLastPrimaryValue, formattedLastSecondaryValue, percentChangeLastValues];
  };

  const [mainMetric, secondaryMetric, rawChange] = getMainAndSecondaryMetrics();

  const change = formatMetric(Math.abs(rawChange as number), METRICTYPE.PERCENT, {
    decimalPlaces: 2
  });

  const getExportDataBlob = async () => {
    try {
      const data = convertSplineChartV2SeriesToDelimitedData(
        primarySeries,
        comparisonSeries,
        primaryLegendDisplayName,
        comparisonLegendDisplayName
      );
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${title || 'chart'}.csv`);
    } catch (e) {
      console.error('Export CSV Error:', e);
    }
  };

  const getBeaconChartWithLegendProps = (): Omit<BeaconChartWithLegendProps, 'children'> => {
    const defaultProps = {
      title,
      primaryLegendProps: {
        displayName: primaryLegendDisplayName,
        metric: mainMetric,
        change,
        direction: getDirection(rawChange)
      },
      comparisonLegendProps: {
        displayName: comparisonLegendDisplayName,
        metric: secondaryMetric,
        direction: getDirection(rawChange)
      },
      loading: isLoading,
      loadingComponent: <SplineChartLoading {...splineChartLoadingProps} />,
      showTitleSummary,
      buildDropdownMenuOptions,
      dropdownOptionHandler,
      convertSeriesToDelimitedData: getExportDataBlob,
      ...chartWithLegendProps
    };

    return beaconChartWithLegendOverride
      ? beaconChartWithLegendOverride(defaultProps, { ...metricByValues, field: metricType } as any)
      : defaultProps;
  };

  const noData = !chartPrimaryData?.length && !chartSecondaryData?.length && !isLoading;

  return (
    <BeaconChartWithLegend {...getBeaconChartWithLegendProps()}>
      {noData ? (
        <NoDataPlaceHolder
          title="Data currently unavailable"
          description="There is currently no data matching your selected criteria. Please try another query to view insights."
          flexProps={{ margin: '80px auto' }}
        />
      ) : (
        <SplineChartV2
          primarySeries={primarySeries}
          comparisonSeries={comparisonSeries}
          metricType={metricTypeDisplay}
          {...splineChartProps}
        />
      )}
    </BeaconChartWithLegend>
  );
};

export default AtlasV2DorisSplineChart;
